import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  defaultDocumentTypeOptions,
  defaultDocumentTypeOptionsGroup,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { DocumentPayload, DocumentType } from '@nxsys/types/models/document';
import type { FormProps } from 'antd';
import { Form } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

type UpdateModalProps = {
  isOpen: boolean;
  title: string;
  documentSelected: DocumentType | null;
  toggle: () => void;
  onUpdateDocument: (documentId: string, params: DocumentPayload) => void;
};

const UpdateDocumentModal: React.FC<UpdateModalProps> = ({
  title,
  isOpen,
  documentSelected,
  toggle,
  onUpdateDocument,
}: UpdateModalProps) => {
  const [form] = Form.useForm();
  const [isSubmitPending, setIsSubmitPending] = useState(false);
  const { messages } = useTypedIntl();
  const { isBureau } = useSelector((state: AppState) => state.appStore);

  useEffect(() => {
    if (documentSelected) {
      form.setFieldsValue({
        subcontractorRef: documentSelected?.subcontractorRef
          ? {
              ...documentSelected.subcontractorRef,
              value: documentSelected.subcontractorRef.id || null,
              label: `${documentSelected.subcontractorRef.forename} ${documentSelected?.subcontractorRef.surname}`,
            }
          : null,
        documentTitle: documentSelected?.documentTitle || null,
        documentType:
          defaultDocumentTypeOptions.find(
            (item) => item.value === documentSelected?.documentType,
          ) || null,
        expiryDate: documentSelected?.expiryDate || null,
        documentDate: documentSelected?.documentDate || null,
        documentFile: documentSelected?.documentFile,
      });
    } else {
      form.resetFields();
    }
  }, [documentSelected]);

  const onCancel = () => {
    console.log('Cancel');
    form.resetFields();
    toggle();
  };

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.log('Success:', values);
    try {
      setIsSubmitPending(true);
      if (!documentSelected) return;
      const params = {
        ...values,
        subcontractorRef: values?.subcontractorRef?.value || null,
        documentType: values?.documentType?.value || null,
        expiryDate: values?.expiryDate?.toDate() || null,
        documentDate: values?.documentDate?.toDate() || null,
      };
      await onUpdateDocument(documentSelected?.id, params);
      onCancel();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitPending(false);
    }
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  console.log('Document', documentSelected);
  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          buttonType: 'primary',
          onClick: onCancel,
        },
        {
          name: 'Save',
          buttonType: 'primary',
          onClick: () => {
            console.log('Create');
            form.submit();
          },
          loading: isSubmitPending,
          disabled: isSubmitPending,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          label={messages['common.documentTitle']}
          name="documentTitle"
          rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={messages['common.documentType']}
          name="documentType"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppSelect
            options={
              isBureau
                ? defaultDocumentTypeOptionsGroup?.filter(
                    (item) => item.value !== 'AGENCY_QUESTIONNAIRE',
                  )
                : defaultDocumentTypeOptionsGroup
            }
            placeholder={messages['form.select']}
          />
        </Form.Item>
        <Form.Item label={messages['common.expiryDate']} name="expiryDate">
          <AppDatePicker />
        </Form.Item>
        <Form.Item label={messages['common.documentDate']} name="documentDate">
          <AppDatePicker />
        </Form.Item>
        <Form.Item
          label={messages['common.attachment']}
          name="documentFile"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppUploadFile
            urlFile={documentSelected?.documentFile}
            onChange={(file: File) => {
              form.setFieldsValue({ documentFile: file });
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default UpdateDocumentModal;
